import {
  BentoCard,
  BentoCardContent,
  BentoCardFooter,
} from '@/app/_components/core/bento-card';
import { cn } from '@/app/_lib/utils';
import {
  CardImage,
  CardButton,
  CardCategories,
  CardTitleBlock,
  CardDescription,
  CardDateLocation,
} from './components';
import {
  type CardVariant,
  VARIANT_CONFIG,
  cardVariants,
  imageVariants,
  containerVariants,
  contentVariants,
} from './event-card.variants';
import type { EventCardProps } from './event-card.types';

// =============================================================================
// Main Component
// =============================================================================

export interface EventCardComponentProps extends Omit<
  EventCardProps,
  'variant'
> {
  variant?: CardVariant;
  sizes: string;
  useTitleAsHeadline?: boolean;
  trackingPrefix?: string;
}

export function EventCard({
  event,
  buttonLabel,
  variant = 'default',
  className,
  sizes,
  useTitleAsHeadline = false,
  trackingPrefix,
}: EventCardComponentProps) {
  const config = VARIANT_CONFIG[variant];

  return (
    <BentoCard
      as="article"
      data-component="event-card"
      data-variant={variant}
      className={cn(cardVariants({ variant }), className)}
    >
      <CardImage
        event={event}
        variant={variant}
        sizes={sizes}
        className={imageVariants({ variant })}
        trackingPrefix={trackingPrefix}
      />

      <BentoCardContent className={containerVariants({ variant })}>
        {config.showDateOnTop && (
          <>
            <div className={cn(!useTitleAsHeadline && 'lg:hidden')}>
              <CardDateLocation
                startDateTime={event.startDateTime}
                endDateTime={event.endDateTime}
                location={event.location}
                filterLocation={event.filterLocation}
                prominent
              />
            </div>
            {!useTitleAsHeadline && (
              <div className="hidden lg:block">
                <CardTitleBlock
                  title={event.title}
                  maxLines={config.titleMaxLines}
                  variant={variant}
                  className="text-2xl font-extrabold"
                />
              </div>
            )}
          </>
        )}

        <div className={contentVariants({ variant })}>
          <CardCategories
            category={event.category}
            subCategory={event.subCategory}
          />
          <div className={cn(!useTitleAsHeadline && 'lg:hidden')}>
            <CardTitleBlock
              title={event.title}
              maxLines={config.titleMaxLines}
              variant={variant}
            />
          </div>
          {!useTitleAsHeadline && (
            <div className="hidden lg:block">
              <CardDateLocation
                startDateTime={event.startDateTime}
                endDateTime={event.endDateTime}
                location={event.location}
                filterLocation={event.filterLocation}
                prominent={config.showDateOnTop}
                asTitle
              />
            </div>
          )}
          {config.showDescription && event.description && (
            <CardDescription
              description={event.description}
              variant={variant}
            />
          )}
        </div>

        <BentoCardFooter className="mt-auto min-w-0 flex-col items-stretch p-0">
          {!config.showDateOnTop && (
            <>
              <div className={cn(!useTitleAsHeadline && 'lg:hidden')}>
                <CardDateLocation
                  startDateTime={event.startDateTime}
                  endDateTime={event.endDateTime}
                  location={event.location}
                  filterLocation={event.filterLocation}
                />
              </div>
              {!useTitleAsHeadline && (
                <div className="hidden lg:block">
                  <CardTitleBlock
                    title={event.title}
                    maxLines={config.titleMaxLines}
                    variant={variant}
                    className="text-base font-normal"
                  />
                </div>
              )}
            </>
          )}
          {event.detailLink && (
            <CardButton
              detailLink={event.detailLink}
              label={buttonLabel}
              size={config.buttonSize}
              className="w-full"
              trackingPrefix={trackingPrefix}
            />
          )}
        </BentoCardFooter>
      </BentoCardContent>
    </BentoCard>
  );
}
